import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Shield, MapPin, Check, AlertCircle, FileText, Upload, CreditCard, Bike, ChevronRight, Filter, X } from 'lucide-react';

// Mock data - Dans la vraie app, cela viendra de l'API
const MOCK_MODELS = [
  {
    id: 1,
    name: "Harley-Davidson Street Bob",
    category: "Cruiser",
    engine: "1868cc",
    price: 120,
    image: "https://images.unsplash.com/photo-1558981852-426c6c22a060?w=800&auto=format&fit=crop",
    features: ["ABS", "Cruise Control", "LED"],
    units: [
      { plate: "AB-123-CD", available: true },
      { plate: "EF-456-GH", available: true }
    ]
  },
  {
    id: 2,
    name: "BMW R 1250 GS",
    category: "Adventure",
    engine: "1254cc",
    price: 150,
    image: "https://images.unsplash.com/photo-1599819177523-e0c91026c4ee?w=800&auto=format&fit=crop",
    features: ["ABS Pro", "Cruise Control", "TFT Display", "Modes de conduite"],
    units: [
      { plate: "IJ-789-KL", available: true }
    ]
  },
  {
    id: 3,
    name: "Yamaha MT-09",
    category: "Roadster",
    engine: "890cc",
    price: 95,
    image: "https://images.unsplash.com/photo-1609630875171-b1321377ee65?w=800&auto=format&fit=crop",
    features: ["ABS", "Contrôle de traction", "LED"],
    units: [
      { plate: "MN-012-OP", available: true },
      { plate: "QR-345-ST", available: true }
    ]
  },
  {
    id: 4,
    name: "Ducati Scrambler 800",
    category: "Scrambler",
    engine: "803cc",
    price: 110,
    image: "https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?w=800&auto=format&fit=crop",
    features: ["ABS", "Modes de conduite", "USB"],
    units: [
      { plate: "UV-678-WX", available: true }
    ]
  },
  {
    id: 5,
    name: "Kawasaki Z900",
    category: "Roadster",
    engine: "948cc",
    price: 105,
    image: "https://images.unsplash.com/photo-1558980664-769d59546b3d?w=800&auto=format&fit=crop",
    features: ["ABS", "LED", "TFT Display"],
    units: [
      { plate: "YZ-901-AB", available: true }
    ]
  }
];

const OPTIONS = [
  { id: "insurance_premium", name: "Assurance Premium", price: 15, description: "Franchise réduite à 300€" },
  { id: "helmet", name: "Casque intégral", price: 5, description: "Casque homologué haute qualité" },
  { id: "jacket", name: "Blouson moto", price: 8, description: "Blouson avec protections" },
  { id: "gps", name: "GPS moto", price: 10, description: "Navigation spécial deux-roues" },
  { id: "km_unlimited", name: "Kilométrage illimité", price: 25, description: "Au lieu de 200km/jour" }
];

const DarcosMotosApp = () => {
  const [step, setStep] = useState('browse'); // browse, booking, confirmation, documents
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [timeRange, setTimeRange] = useState({ start: '09:00', end: '18:00' });
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [clientInfo, setClientInfo] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [contractAccepted, setContractAccepted] = useState(false);
  const [reservationNumber, setReservationNumber] = useState(null);
  const [documents, setDocuments] = useState({ license: null, id: null, address: null });
  const [deliveryAddress, setDeliveryAddress] = useState('');

  // Calcul de la durée et du prix
  const calculateDuration = () => {
    if (!dateRange.start || !dateRange.end) return 0;
    const start = new Date(dateRange.start + 'T' + timeRange.start);
    const end = new Date(dateRange.end + 'T' + timeRange.end);
    const hours = (end - start) / (1000 * 60 * 60);
    return Math.max(hours / 24, 0.5); // Minimum 0.5 jour
  };

  const calculatePrice = () => {
    if (!selectedModel) return 0;
    const duration = calculateDuration();
    const basePrice = selectedModel.price * duration;
    const optionsPrice = selectedOptions.reduce((sum, optId) => {
      const option = OPTIONS.find(o => o.id === optId);
      return sum + (option ? option.price * duration : 0);
    }, 0);
    return Math.round(basePrice + optionsPrice);
  };

  const toggleOption = (optionId) => {
    setSelectedOptions(prev => 
      prev.includes(optionId) 
        ? prev.filter(id => id !== optionId)
        : [...prev, optionId]
    );
  };

  const proceedToBooking = () => {
    if (selectedModel && dateRange.start && dateRange.end) {
      setStep('booking');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const submitBooking = () => {
    // Simulation de paiement et création de réservation
    const resNumber = 'DM' + Date.now().toString().slice(-8);
    setReservationNumber(resNumber);
    setStep('confirmation');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submitDocuments = () => {
    alert('Documents envoyés avec succès ! DARCOS va les vérifier et vous contactera.');
  };

  // Browse Step - Liste des motos
  const BrowseStep = () => (
    <div className="browse-container">
      <header className="hero">
        <div className="hero-content">
          <h1 className="brand-title">DARCOS</h1>
          <p className="hero-subtitle">Location de motos d'exception</p>
          <div className="hero-tagline">Liberté · Passion · Aventure</div>
        </div>
      </header>

      <div className="filters-section">
        <div className="filters-card">
          <h3><Filter size={20} /> Filtrer par disponibilité</h3>
          <div className="filter-grid">
            <div className="filter-group">
              <label>Date de début</label>
              <input 
                type="date" 
                value={dateRange.start}
                min={new Date().toISOString().split('T')[0]}
                onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
              />
              <input 
                type="time" 
                value={timeRange.start}
                onChange={(e) => setTimeRange({...timeRange, start: e.target.value})}
              />
            </div>
            <div className="filter-group">
              <label>Date de fin</label>
              <input 
                type="date" 
                value={dateRange.end}
                min={dateRange.start || new Date().toISOString().split('T')[0]}
                onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
              />
              <input 
                type="time" 
                value={timeRange.end}
                onChange={(e) => setTimeRange({...timeRange, end: e.target.value})}
              />
            </div>
          </div>
          {dateRange.start && dateRange.end && (
            <div className="duration-display">
              <Clock size={16} />
              <span>Durée : {calculateDuration().toFixed(1)} jours</span>
            </div>
          )}
        </div>
      </div>

      <div className="models-grid">
        {MOCK_MODELS.map(model => (
          <div key={model.id} className="model-card" onClick={() => setSelectedModel(model)}>
            <div className="model-image" style={{backgroundImage: `url(${model.image})`}}>
              <span className="model-category">{model.category}</span>
            </div>
            <div className="model-info">
              <h3>{model.name}</h3>
              <div className="model-engine">{model.engine}</div>
              <div className="model-features">
                {model.features.slice(0, 3).map((f, i) => (
                  <span key={i} className="feature-tag">{f}</span>
                ))}
              </div>
              <div className="model-footer">
                <div className="price-display">
                  <span className="price">{dateRange.start && dateRange.end ? calculateDuration() * model.price : model.price}€</span>
                  <span className="price-unit">{dateRange.start && dateRange.end ? 'total' : '/jour'}</span>
                </div>
                <button className="btn-select">Sélectionner <ChevronRight size={16} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal de sélection */}
      {selectedModel && (
        <div className="modal-overlay" onClick={() => setSelectedModel(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedModel(null)}>
              <X size={24} />
            </button>
            
            <div className="modal-header">
              <img src={selectedModel.image} alt={selectedModel.name} className="modal-image" />
              <div className="modal-title-section">
                <h2>{selectedModel.name}</h2>
                <div className="modal-specs">
                  <span><Bike size={18} /> {selectedModel.category}</span>
                  <span>{selectedModel.engine}</span>
                </div>
              </div>
            </div>

            <div className="modal-body">
              <div className="options-section">
                <h3>Options disponibles</h3>
                <div className="options-list">
                  {OPTIONS.map(option => (
                    <div 
                      key={option.id} 
                      className={`option-item ${selectedOptions.includes(option.id) ? 'selected' : ''}`}
                      onClick={() => toggleOption(option.id)}
                    >
                      <div className="option-checkbox">
                        {selectedOptions.includes(option.id) && <Check size={16} />}
                      </div>
                      <div className="option-details">
                        <div className="option-name">{option.name}</div>
                        <div className="option-desc">{option.description}</div>
                      </div>
                      <div className="option-price">
                        +{dateRange.start && dateRange.end ? Math.round(option.price * calculateDuration()) : option.price}€
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="booking-summary">
                <div className="summary-row">
                  <span>Tarif de base</span>
                  <span>{dateRange.start && dateRange.end ? Math.round(selectedModel.price * calculateDuration()) : selectedModel.price}€</span>
                </div>
                {selectedOptions.map(optId => {
                  const option = OPTIONS.find(o => o.id === optId);
                  return option ? (
                    <div key={optId} className="summary-row">
                      <span>{option.name}</span>
                      <span>+{dateRange.start && dateRange.end ? Math.round(option.price * calculateDuration()) : option.price}€</span>
                    </div>
                  ) : null;
                })}
                <div className="summary-row total">
                  <span>Total</span>
                  <span>{calculatePrice()}€</span>
                </div>
              </div>

              {!dateRange.start || !dateRange.end ? (
                <div className="warning-box">
                  <AlertCircle size={20} />
                  <span>Veuillez sélectionner des dates pour continuer</span>
                </div>
              ) : (
                <button className="btn-primary" onClick={proceedToBooking}>
                  Réserver cette moto
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Booking Step - Formulaire de réservation
  const BookingStep = () => (
    <div className="booking-container">
      <div className="booking-progress">
        <div className="progress-step active">1. Informations</div>
        <div className="progress-step">2. Paiement</div>
        <div className="progress-step">3. Confirmation</div>
      </div>

      <div className="booking-layout">
        <div className="booking-form">
          <h2>Vos coordonnées</h2>
          
          <div className="form-grid">
            <div className="form-group">
              <label>Prénom *</label>
              <input 
                type="text" 
                value={clientInfo.firstName}
                onChange={(e) => setClientInfo({...clientInfo, firstName: e.target.value})}
                placeholder="Jean"
              />
            </div>
            <div className="form-group">
              <label>Nom *</label>
              <input 
                type="text" 
                value={clientInfo.lastName}
                onChange={(e) => setClientInfo({...clientInfo, lastName: e.target.value})}
                placeholder="Dupont"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Email *</label>
            <input 
              type="email" 
              value={clientInfo.email}
              onChange={(e) => setClientInfo({...clientInfo, email: e.target.value})}
              placeholder="jean.dupont@email.com"
            />
          </div>

          <div className="form-group">
            <label>Téléphone *</label>
            <input 
              type="tel" 
              value={clientInfo.phone}
              onChange={(e) => setClientInfo({...clientInfo, phone: e.target.value})}
              placeholder="06 12 34 56 78"
            />
          </div>

          <div className="contract-acceptance">
            <label className="checkbox-label">
              <input 
                type="checkbox" 
                checked={contractAccepted}
                onChange={(e) => setContractAccepted(e.target.checked)}
              />
              <span>
                J'accepte les <a href="#cgv" target="_blank">Conditions Générales de Vente</a> et 
                le <a href="#contract" target="_blank">Contrat de Location</a>
              </span>
            </label>
          </div>

          <div className="form-actions">
            <button className="btn-secondary" onClick={() => setStep('browse')}>
              Retour
            </button>
            <button 
              className="btn-primary" 
              onClick={submitBooking}
              disabled={!clientInfo.firstName || !clientInfo.lastName || !clientInfo.email || !clientInfo.phone || !contractAccepted}
            >
              Payer l'acompte (50€)
            </button>
          </div>
        </div>

        <div className="booking-sidebar">
          <div className="summary-card">
            <h3>Récapitulatif</h3>
            <img src={selectedModel.image} alt={selectedModel.name} className="summary-image" />
            <h4>{selectedModel.name}</h4>
            
            <div className="summary-details">
              <div className="detail-row">
                <Calendar size={16} />
                <div>
                  <div className="detail-label">Début</div>
                  <div className="detail-value">{dateRange.start} à {timeRange.start}</div>
                </div>
              </div>
              <div className="detail-row">
                <Calendar size={16} />
                <div>
                  <div className="detail-label">Fin</div>
                  <div className="detail-value">{dateRange.end} à {timeRange.end}</div>
                </div>
              </div>
              <div className="detail-row">
                <Clock size={16} />
                <div>
                  <div className="detail-label">Durée</div>
                  <div className="detail-value">{calculateDuration().toFixed(1)} jours</div>
                </div>
              </div>
            </div>

            {selectedOptions.length > 0 && (
              <div className="summary-options">
                <h5>Options sélectionnées</h5>
                {selectedOptions.map(optId => {
                  const option = OPTIONS.find(o => o.id === optId);
                  return option ? <div key={optId} className="option-summary">{option.name}</div> : null;
                })}
              </div>
            )}

            <div className="summary-pricing">
              <div className="pricing-row">
                <span>Total location</span>
                <span>{calculatePrice()}€</span>
              </div>
              <div className="pricing-row">
                <span>Acompte (à payer maintenant)</span>
                <span className="highlight">50€</span>
              </div>
              <div className="pricing-row">
                <span>Caution (préautorisation)</span>
                <span>800€</span>
              </div>
              <div className="pricing-row total">
                <span>Solde (à la livraison)</span>
                <span>{calculatePrice() - 50}€</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Confirmation Step
  const ConfirmationStep = () => (
    <div className="confirmation-container">
      <div className="confirmation-card">
        <div className="confirmation-icon">
          <Check size={48} />
        </div>
        <h2>Demande de réservation enregistrée !</h2>
        <p className="confirmation-message">
          Votre demande est en cours de traitement par notre équipe DARCOS. 
          Vous recevrez un email de confirmation sous 24h.
        </p>

        <div className="reservation-number">
          <span className="label">Numéro de réservation</span>
          <span className="number">{reservationNumber}</span>
        </div>

        <div className="confirmation-details">
          <div className="detail-card">
            <h4>{selectedModel.name}</h4>
            <div className="detail-info">
              <div><Calendar size={16} /> Du {dateRange.start} au {dateRange.end}</div>
              <div><Clock size={16} /> {timeRange.start} - {timeRange.end}</div>
            </div>
          </div>

          <div className="payment-recap">
            <h4>Paiements</h4>
            <div className="payment-row">
              <Check size={16} className="paid" />
              <span>Acompte versé</span>
              <span>50€</span>
            </div>
            <div className="payment-row pending">
              <Clock size={16} />
              <span>Solde à régler</span>
              <span>{calculatePrice() - 50}€</span>
            </div>
            <div className="payment-row">
              <Shield size={16} />
              <span>Caution (préautorisation)</span>
              <span>800€</span>
            </div>
          </div>
        </div>

        <div className="next-steps">
          <h4>Prochaines étapes</h4>
          <ol>
            <li>DARCOS valide votre demande (sous 24h)</li>
            <li>Vous recevez un email de confirmation avec un lien</li>
            <li>Complétez votre dossier avec les justificatifs</li>
            <li>Attendez la validation finale</li>
            <li>Profitez de votre moto !</li>
          </ol>
        </div>

        <button className="btn-primary" onClick={() => setStep('documents')}>
          Compléter mon dossier maintenant
        </button>
      </div>
    </div>
  );

  // Documents Step
  const DocumentsStep = () => {
    const handleFileChange = (docType, file) => {
      setDocuments({...documents, [docType]: file});
    };

    return (
      <div className="documents-container">
        <div className="documents-card">
          <h2>Complément de dossier</h2>
          <p className="documents-intro">
            Merci de fournir les documents suivants pour finaliser votre réservation.
          </p>

          <div className="form-group">
            <label>Numéro de réservation *</label>
            <input type="text" value={reservationNumber} readOnly />
          </div>

          <div className="form-group">
            <label>Adresse précise de livraison *</label>
            <input 
              type="text" 
              value={deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
              placeholder="12 Rue de la Liberté, 75001 Paris"
            />
            <div className="input-hint">
              <MapPin size={14} />
              <span>Le véhicule vous sera livré à cette adresse</span>
            </div>
          </div>

          <div className="documents-upload">
            <h4>Documents requis</h4>
            
            <div className="upload-item">
              <div className="upload-info">
                <FileText size={20} />
                <div>
                  <div className="upload-label">Permis de conduire *</div>
                  <div className="upload-desc">Recto et verso en un seul fichier</div>
                </div>
              </div>
              <label className="btn-upload">
                <Upload size={16} />
                {documents.license ? documents.license.name : 'Choisir un fichier'}
                <input 
                  type="file" 
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileChange('license', e.target.files[0])}
                  style={{display: 'none'}}
                />
              </label>
            </div>

            <div className="upload-item">
              <div className="upload-info">
                <FileText size={20} />
                <div>
                  <div className="upload-label">Pièce d'identité *</div>
                  <div className="upload-desc">CNI ou passeport</div>
                </div>
              </div>
              <label className="btn-upload">
                <Upload size={16} />
                {documents.id ? documents.id.name : 'Choisir un fichier'}
                <input 
                  type="file" 
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileChange('id', e.target.files[0])}
                  style={{display: 'none'}}
                />
              </label>
            </div>

            <div className="upload-item">
              <div className="upload-info">
                <FileText size={20} />
                <div>
                  <div className="upload-label">Justificatif de domicile *</div>
                  <div className="upload-desc">Moins de 3 mois</div>
                </div>
              </div>
              <label className="btn-upload">
                <Upload size={16} />
                {documents.address ? documents.address.name : 'Choisir un fichier'}
                <input 
                  type="file" 
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileChange('address', e.target.files[0])}
                  style={{display: 'none'}}
                />
              </label>
            </div>
          </div>

          <button 
            className="btn-primary" 
            onClick={submitDocuments}
            disabled={!deliveryAddress || !documents.license || !documents.id || !documents.address}
          >
            Envoyer les documents
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="darcos-app">
      {step === 'browse' && <BrowseStep />}
      {step === 'booking' && <BookingStep />}
      {step === 'confirmation' && <ConfirmationStep />}
      {step === 'documents' && <DocumentsStep />}
    </div>
  );
};

// Styles CSS
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Work+Sans:wght@300;400;500;600;700&display=swap');

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Work Sans', sans-serif;
    background: #0a0a0a;
    color: #e8e8e8;
    line-height: 1.6;
  }

  .darcos-app {
    min-height: 100vh;
  }

  /* Hero Section */
  .hero {
    background: linear-gradient(135deg, #1a1a1a 0%, #2d1810 100%);
    padding: 120px 20px 80px;
    text-align: center;
    position: relative;
    overflow: hidden;
  }

  .hero::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      repeating-linear-gradient(
        90deg,
        transparent,
        transparent 50px,
        rgba(255,255,255,0.02) 50px,
        rgba(255,255,255,0.02) 51px
      );
  }

  .hero-content {
    position: relative;
    z-index: 1;
    max-width: 800px;
    margin: 0 auto;
  }

  .brand-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 120px;
    letter-spacing: 20px;
    color: #ff6b35;
    text-shadow: 
      0 0 20px rgba(255, 107, 53, 0.3),
      0 0 40px rgba(255, 107, 53, 0.2);
    margin-bottom: 10px;
    animation: fadeInDown 1s ease-out;
  }

  .hero-subtitle {
    font-size: 24px;
    font-weight: 300;
    letter-spacing: 4px;
    text-transform: uppercase;
    margin-bottom: 20px;
    animation: fadeInUp 1s ease-out 0.2s backwards;
  }

  .hero-tagline {
    font-size: 16px;
    color: #999;
    letter-spacing: 2px;
    animation: fadeInUp 1s ease-out 0.4s backwards;
  }

  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Filters Section */
  .filters-section {
    padding: 40px 20px;
    max-width: 1200px;
    margin: 0 auto;
  }

  .filters-card {
    background: #1a1a1a;
    border: 1px solid #333;
    border-radius: 12px;
    padding: 30px;
    box-shadow: 0 10px 40px rgba(0,0,0,0.3);
  }

  .filters-card h3 {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 20px;
    margin-bottom: 20px;
    color: #ff6b35;
  }

  .filter-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 20px;
  }

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .filter-group label {
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #999;
  }

  .filter-group input[type="date"],
  .filter-group input[type="time"] {
    background: #0a0a0a;
    border: 1px solid #333;
    border-radius: 8px;
    padding: 12px;
    color: #e8e8e8;
    font-family: 'Work Sans', sans-serif;
    font-size: 16px;
    transition: border-color 0.3s;
  }

  .filter-group input[type="date"]:focus,
  .filter-group input[type="time"]:focus {
    outline: none;
    border-color: #ff6b35;
  }

  .duration-display {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 20px;
    padding: 15px;
    background: rgba(255, 107, 53, 0.1);
    border-left: 3px solid #ff6b35;
    border-radius: 4px;
    color: #ff6b35;
    font-weight: 600;
  }

  /* Models Grid */
  .models-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 30px;
    padding: 40px 20px;
    max-width: 1400px;
    margin: 0 auto;
  }

  .model-card {
    background: #1a1a1a;
    border: 1px solid #333;
    border-radius: 16px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    animation: fadeIn 0.6s ease-out backwards;
  }

  .model-card:nth-child(1) { animation-delay: 0.1s; }
  .model-card:nth-child(2) { animation-delay: 0.2s; }
  .model-card:nth-child(3) { animation-delay: 0.3s; }
  .model-card:nth-child(4) { animation-delay: 0.4s; }
  .model-card:nth-child(5) { animation-delay: 0.5s; }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .model-card:hover {
    transform: translateY(-8px);
    border-color: #ff6b35;
    box-shadow: 0 20px 60px rgba(255, 107, 53, 0.2);
  }

  .model-image {
    height: 220px;
    background-size: cover;
    background-position: center;
    position: relative;
  }

  .model-category {
    position: absolute;
    top: 15px;
    right: 15px;
    background: rgba(255, 107, 53, 0.9);
    backdrop-filter: blur(10px);
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .model-info {
    padding: 25px;
  }

  .model-info h3 {
    font-size: 20px;
    margin-bottom: 8px;
    color: #e8e8e8;
  }

  .model-engine {
    color: #999;
    font-size: 14px;
    margin-bottom: 15px;
    font-weight: 500;
  }

  .model-features {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 20px;
  }

  .feature-tag {
    background: rgba(255, 107, 53, 0.1);
    color: #ff6b35;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
  }

  .model-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid #333;
  }

  .price-display {
    display: flex;
    flex-direction: column;
  }

  .price {
    font-size: 28px;
    font-weight: 700;
    color: #ff6b35;
  }

  .price-unit {
    font-size: 12px;
    color: #999;
  }

  .btn-select {
    display: flex;
    align-items: center;
    gap: 5px;
    background: #ff6b35;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 8px;
    font-family: 'Work Sans', sans-serif;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
  }

  .btn-select:hover {
    background: #ff8555;
    transform: translateX(3px);
  }

  /* Modal */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.9);
    backdrop-filter: blur(5px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
    animation: fadeIn 0.3s;
  }

  .modal-content {
    background: #1a1a1a;
    border: 1px solid #333;
    border-radius: 16px;
    max-width: 700px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
    animation: slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(50px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .modal-close {
    position: absolute;
    top: 20px;
    right: 20px;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: white;
    transition: all 0.3s;
    z-index: 10;
  }

  .modal-close:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: rotate(90deg);
  }

  .modal-header {
    position: relative;
  }

  .modal-image {
    width: 100%;
    height: 300px;
    object-fit: cover;
    border-radius: 16px 16px 0 0;
  }

  .modal-title-section {
    padding: 30px;
    background: linear-gradient(to bottom, transparent, #1a1a1a 80%);
    margin-top: -80px;
    position: relative;
  }

  .modal-title-section h2 {
    font-size: 32px;
    margin-bottom: 10px;
  }

  .modal-specs {
    display: flex;
    gap: 20px;
    color: #999;
    font-size: 14px;
  }

  .modal-specs span {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .modal-body {
    padding: 0 30px 30px;
  }

  .options-section h3 {
    font-size: 20px;
    margin-bottom: 20px;
    color: #ff6b35;
  }

  .options-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 30px;
  }

  .option-item {
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 15px;
    background: #0a0a0a;
    border: 1px solid #333;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s;
  }

  .option-item:hover {
    border-color: #ff6b35;
  }

  .option-item.selected {
    background: rgba(255, 107, 53, 0.1);
    border-color: #ff6b35;
  }

  .option-checkbox {
    width: 24px;
    height: 24px;
    border: 2px solid #333;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.3s;
  }

  .option-item.selected .option-checkbox {
    background: #ff6b35;
    border-color: #ff6b35;
    color: white;
  }

  .option-details {
    flex: 1;
  }

  .option-name {
    font-weight: 600;
    margin-bottom: 4px;
  }

  .option-desc {
    font-size: 13px;
    color: #999;
  }

  .option-price {
    font-weight: 700;
    color: #ff6b35;
  }

  .booking-summary {
    background: #0a0a0a;
    border: 1px solid #333;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
  }

  .summary-row {
    display: flex;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid #222;
  }

  .summary-row:last-child {
    border-bottom: none;
  }

  .summary-row.total {
    font-size: 20px;
    font-weight: 700;
    color: #ff6b35;
    padding-top: 15px;
    margin-top: 10px;
    border-top: 2px solid #333;
  }

  .warning-box {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 15px;
    background: rgba(255, 193, 7, 0.1);
    border-left: 3px solid #ffc107;
    border-radius: 4px;
    color: #ffc107;
    margin-bottom: 20px;
  }

  .btn-primary {
    width: 100%;
    background: #ff6b35;
    color: white;
    border: none;
    padding: 16px 32px;
    border-radius: 8px;
    font-family: 'Work Sans', sans-serif;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
  }

  .btn-primary:hover:not(:disabled) {
    background: #ff8555;
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(255, 107, 53, 0.3);
  }

  .btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-secondary {
    background: transparent;
    color: #e8e8e8;
    border: 1px solid #333;
    padding: 16px 32px;
    border-radius: 8px;
    font-family: 'Work Sans', sans-serif;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
  }

  .btn-secondary:hover {
    border-color: #ff6b35;
    color: #ff6b35;
  }

  /* Booking Step */
  .booking-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 40px 20px;
  }

  .booking-progress {
    display: flex;
    justify-content: center;
    gap: 30px;
    margin-bottom: 50px;
    padding: 30px;
    background: #1a1a1a;
    border-radius: 12px;
  }

  .progress-step {
    padding: 10px 20px;
    border-radius: 8px;
    font-weight: 600;
    color: #666;
  }

  .progress-step.active {
    background: rgba(255, 107, 53, 0.2);
    color: #ff6b35;
  }

  .booking-layout {
    display: grid;
    grid-template-columns: 1fr 400px;
    gap: 40px;
  }

  @media (max-width: 968px) {
    .booking-layout {
      grid-template-columns: 1fr;
    }
  }

  .booking-form {
    background: #1a1a1a;
    border: 1px solid #333;
    border-radius: 12px;
    padding: 40px;
  }

  .booking-form h2 {
    margin-bottom: 30px;
    font-size: 28px;
  }

  .form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;
  }

  @media (max-width: 768px) {
    .form-grid {
      grid-template-columns: 1fr;
    }
  }

  .form-group {
    margin-bottom: 20px;
  }

  .form-group label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: #999;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .form-group input {
    width: 100%;
    background: #0a0a0a;
    border: 1px solid #333;
    border-radius: 8px;
    padding: 14px;
    color: #e8e8e8;
    font-family: 'Work Sans', sans-serif;
    font-size: 16px;
    transition: border-color 0.3s;
  }

  .form-group input:focus {
    outline: none;
    border-color: #ff6b35;
  }

  .contract-acceptance {
    margin: 30px 0;
    padding: 20px;
    background: rgba(255, 107, 53, 0.05);
    border: 1px solid rgba(255, 107, 53, 0.2);
    border-radius: 8px;
  }

  .checkbox-label {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: pointer;
  }

  .checkbox-label input[type="checkbox"] {
    margin-top: 4px;
    width: 20px;
    height: 20px;
    cursor: pointer;
  }

  .checkbox-label a {
    color: #ff6b35;
    text-decoration: none;
  }

  .checkbox-label a:hover {
    text-decoration: underline;
  }

  .form-actions {
    display: flex;
    gap: 20px;
    margin-top: 30px;
  }

  .form-actions button {
    flex: 1;
  }

  .booking-sidebar {
    position: sticky;
    top: 20px;
    height: fit-content;
  }

  .summary-card {
    background: #1a1a1a;
    border: 1px solid #333;
    border-radius: 12px;
    padding: 30px;
  }

  .summary-card h3 {
    margin-bottom: 20px;
    font-size: 22px;
    color: #ff6b35;
  }

  .summary-image {
    width: 100%;
    height: 180px;
    object-fit: cover;
    border-radius: 8px;
    margin-bottom: 15px;
  }

  .summary-card h4 {
    margin-bottom: 20px;
    font-size: 18px;
  }

  .summary-details {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-bottom: 20px;
  }

  .detail-row {
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }

  .detail-row svg {
    flex-shrink: 0;
    margin-top: 3px;
    color: #ff6b35;
  }

  .detail-label {
    font-size: 12px;
    color: #999;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .detail-value {
    font-weight: 600;
    margin-top: 2px;
  }

  .summary-options {
    margin: 20px 0;
    padding: 15px;
    background: #0a0a0a;
    border-radius: 8px;
  }

  .summary-options h5 {
    font-size: 14px;
    color: #999;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 10px;
  }

  .option-summary {
    padding: 8px 0;
    font-size: 14px;
    border-bottom: 1px solid #222;
  }

  .option-summary:last-child {
    border-bottom: none;
  }

  .summary-pricing {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 2px solid #333;
  }

  .pricing-row {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    font-size: 14px;
  }

  .pricing-row.total {
    font-size: 18px;
    font-weight: 700;
    color: #ff6b35;
    padding-top: 15px;
    margin-top: 10px;
    border-top: 1px solid #333;
  }

  .pricing-row .highlight {
    color: #ff6b35;
    font-weight: 700;
  }

  /* Confirmation Step */
  .confirmation-container {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
  }

  .confirmation-card {
    max-width: 700px;
    width: 100%;
    background: #1a1a1a;
    border: 1px solid #333;
    border-radius: 16px;
    padding: 50px;
    text-align: center;
    animation: slideUp 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .confirmation-icon {
    width: 100px;
    height: 100px;
    background: rgba(76, 175, 80, 0.2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 30px;
    color: #4caf50;
  }

  .confirmation-card h2 {
    font-size: 32px;
    margin-bottom: 20px;
  }

  .confirmation-message {
    color: #999;
    margin-bottom: 40px;
    font-size: 16px;
    line-height: 1.6;
  }

  .reservation-number {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;
    background: rgba(255, 107, 53, 0.1);
    border: 2px dashed #ff6b35;
    border-radius: 8px;
    margin-bottom: 40px;
  }

  .reservation-number .label {
    font-size: 12px;
    color: #999;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .reservation-number .number {
    font-size: 28px;
    font-weight: 700;
    color: #ff6b35;
    font-family: 'Bebas Neue', sans-serif;
    letter-spacing: 3px;
  }

  .confirmation-details {
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 40px;
  }

  .detail-card {
    padding: 20px;
    background: #0a0a0a;
    border: 1px solid #333;
    border-radius: 8px;
  }

  .detail-card h4 {
    margin-bottom: 15px;
    font-size: 18px;
    color: #ff6b35;
  }

  .detail-info {
    display: flex;
    flex-direction: column;
    gap: 10px;
    color: #999;
    font-size: 14px;
  }

  .detail-info div {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .payment-recap {
    padding: 20px;
    background: #0a0a0a;
    border: 1px solid #333;
    border-radius: 8px;
  }

  .payment-recap h4 {
    margin-bottom: 15px;
    font-size: 18px;
    color: #ff6b35;
  }

  .payment-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid #222;
  }

  .payment-row:last-child {
    border-bottom: none;
  }

  .payment-row svg {
    flex-shrink: 0;
  }

  .payment-row.paid svg {
    color: #4caf50;
  }

  .payment-row.pending {
    color: #ffc107;
  }

  .payment-row span:first-of-type {
    flex: 1;
  }

  .payment-row span:last-of-type {
    font-weight: 700;
  }

  .next-steps {
    text-align: left;
    padding: 25px;
    background: rgba(255, 107, 53, 0.05);
    border: 1px solid rgba(255, 107, 53, 0.2);
    border-radius: 8px;
    margin-bottom: 30px;
  }

  .next-steps h4 {
    margin-bottom: 15px;
    color: #ff6b35;
  }

  .next-steps ol {
    margin-left: 20px;
    color: #999;
    line-height: 1.8;
  }

  .next-steps li {
    margin-bottom: 8px;
  }

  /* Documents Step */
  .documents-container {
    min-height: 100vh;
    padding: 60px 20px;
    max-width: 800px;
    margin: 0 auto;
  }

  .documents-card {
    background: #1a1a1a;
    border: 1px solid #333;
    border-radius: 16px;
    padding: 50px;
  }

  .documents-card h2 {
    font-size: 32px;
    margin-bottom: 15px;
  }

  .documents-intro {
    color: #999;
    margin-bottom: 40px;
  }

  .input-hint {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    font-size: 13px;
    color: #999;
  }

  .documents-upload {
    margin: 40px 0;
  }

  .documents-upload h4 {
    font-size: 18px;
    margin-bottom: 20px;
    color: #ff6b35;
  }

  .upload-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px;
    background: #0a0a0a;
    border: 1px solid #333;
    border-radius: 8px;
    margin-bottom: 15px;
    transition: border-color 0.3s;
  }

  .upload-item:hover {
    border-color: #ff6b35;
  }

  .upload-info {
    display: flex;
    align-items: center;
    gap: 15px;
    flex: 1;
  }

  .upload-info svg {
    color: #ff6b35;
    flex-shrink: 0;
  }

  .upload-label {
    font-weight: 600;
    margin-bottom: 4px;
  }

  .upload-desc {
    font-size: 13px;
    color: #999;
  }

  .btn-upload {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 107, 53, 0.1);
    color: #ff6b35;
    border: 1px solid #ff6b35;
    padding: 10px 20px;
    border-radius: 6px;
    font-family: 'Work Sans', sans-serif;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
    font-size: 14px;
  }

  .btn-upload:hover {
    background: rgba(255, 107, 53, 0.2);
  }

  /* Responsive */
  @media (max-width: 768px) {
    .brand-title {
      font-size: 60px;
      letter-spacing: 10px;
    }

    .hero-subtitle {
      font-size: 18px;
    }

    .models-grid {
      grid-template-columns: 1fr;
    }

    .modal-content {
      margin: 20px;
    }

    .booking-form,
    .summary-card,
    .confirmation-card,
    .documents-card {
      padding: 30px 20px;
    }

    .upload-item {
      flex-direction: column;
      align-items: flex-start;
      gap: 15px;
    }

    .btn-upload {
      width: 100%;
      justify-content: center;
    }
  }

  /* Scrollbar styling */
  ::-webkit-scrollbar {
    width: 10px;
  }

  ::-webkit-scrollbar-track {
    background: #0a0a0a;
  }

  ::-webkit-scrollbar-thumb {
    background: #333;
    border-radius: 5px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #ff6b35;
  }
`;

export default DarcosMotosApp;

// Injecter les styles
const styleSheet = document.createElement("style");
styleSheet.textContent = styles;
document.head.appendChild(styleSheet);
